import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import { TrendingUp, TrendingDown, Minus, RefreshCw, Car, Palette, Wine, Gem, ShoppingBag, Watch, Wifi, WifiOff, Radio } from 'lucide-react';

const iconMap = {
  car: Car,
  art: Palette,
  wine: Wine,
  jewelry: Gem,
  handbag: ShoppingBag,
  watch: Watch,
};

const categoryColors = {
  car: 'text-emerald-400',
  art: 'text-purple-400',
  wine: 'text-red-400',
  jewelry: 'text-yellow-400',
  handbag: 'text-orange-400',
  watch: 'text-cyan-400',
};

export const MarketIndices = ({ compact = false }) => {
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [updateFlash, setUpdateFlash] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const indicesRef = useRef([]); // Use ref to avoid dependency issues

  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    // Convert http(s) to ws(s)
    const wsUrl = backendUrl.replace(/^http/, 'ws');
    return `${wsUrl}/ws/market`;
  }, []);

  // Track WebSocket connection attempts
  const wsReconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3;

  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    // Stop trying after max attempts - use REST polling instead
    if (wsReconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('WebSocket unavailable, using REST polling');
      return;
    }

    try {
      const wsUrl = getWebSocketUrl();
      console.log('Connecting to WebSocket:', wsUrl);
      wsReconnectAttemptsRef.current += 1;
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        wsReconnectAttemptsRef.current = 0; // Reset on successful connection
        
        // Set up ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'market_update' && message.data) {
            // Track which indices changed using ref to avoid stale closure
            const newFlash = {};
            message.data.forEach(idx => {
              const oldIdx = indicesRef.current.find(i => i.id === idx.id);
              if (oldIdx && oldIdx.current_value !== idx.current_value) {
                newFlash[idx.id] = idx.current_value > oldIdx.current_value ? 'up' : 'down';
              }
            });
            
            setUpdateFlash(newFlash);
            setTimeout(() => setUpdateFlash({}), 1000);
            
            indicesRef.current = message.data; // Update ref
            setIndices(message.data);
            setLastUpdated(new Date(message.timestamp));
            setLoading(false);
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Only reconnect if under max attempts
        if (wsReconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        } else {
          console.log('Max WebSocket reconnect attempts reached, using REST polling');
        }
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      // Fallback to REST API
      fetchIndices();
    }
  }, [getWebSocketUrl]); // Removed indices dependency

  const fetchIndices = async () => {
    try {
      const response = await api.getMarketIndices();
      indicesRef.current = response.data; // Update ref
      setIndices(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch market indices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Polling fallback when WebSocket is not available
  const pollingIntervalRef = useRef(null);
  
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;
    console.log('Starting REST API polling fallback');
    setIsPolling(true);
    pollingIntervalRef.current = setInterval(async () => {
      await fetchIndices();
    }, 3600000); // Update every hour (3600000ms)
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch via REST API
    fetchIndices();
    
    // Try to connect to WebSocket
    connectWebSocket();
    
    // Start polling as fallback after 3 seconds if WebSocket not connected
    const fallbackTimeout = setTimeout(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        startPolling();
      }
    }, 3000);

    return () => {
      clearTimeout(fallbackTimeout);
      stopPolling();
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Stop polling when WebSocket connects
  useEffect(() => {
    if (isConnected) {
      stopPolling();
    }
  }, [isConnected, stopPolling]);

  const getTrendIcon = (change) => {
    if (change > 0.5) return <TrendingUp size={14} className="text-emerald-400" />;
    if (change < -0.5) return <TrendingDown size={14} className="text-red-400" />;
    return <Minus size={14} className="text-muted-foreground" />;
  };

  const formatChange = (change) => {
    const prefix = change >= 0 ? '+' : '';
    return `${prefix}${change.toFixed(2)}%`;
  };

  const getFlashClass = (id) => {
    if (updateFlash[id] === 'up') return 'bg-emerald-500/20 transition-colors duration-500';
    if (updateFlash[id] === 'down') return 'bg-red-500/20 transition-colors duration-500';
    return '';
  };

  if (loading) {
    return (
      <div className="luxury-card p-4 animate-pulse">
        <div className="h-4 bg-[#1A1A1A] rounded w-1/3 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-8 bg-[#1A1A1A] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
        {indices.map((idx) => {
          const Icon = iconMap[idx.category];
          return (
            <div 
              key={idx.id}
              className={`flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-[#0A0A0A] border border-[#1A1A1A] ${getFlashClass(idx.id)}`}
              data-testid={`market-index-compact-${idx.category}`}
              title={idx.description || idx.name}
            >
              <Icon size={14} className={categoryColors[idx.category]} />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">{idx.category === 'wine' ? 'wine & spirits' : idx.category}</span>
              <span className={`text-sm font-mono font-medium ${idx.change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatChange(idx.change_24h)}
              </span>
              {idx.data_source === 'live' ? (
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" title="Live market data" />
              ) : (
                <span className="w-2 h-2 bg-amber-500 rounded-full" title="Estimated from market trends" />
              )}
            </div>
          );
        })}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2">
          {isConnected ? (
            <>
              <Wifi size={12} className="text-emerald-400" />
              <span className="text-xs text-emerald-400">LIVE</span>
            </>
          ) : isPolling ? (
            <>
              <Radio size={12} className="text-gold animate-pulse" />
              <span className="text-xs text-gold">POLLING</span>
            </>
          ) : (
            <>
              <WifiOff size={12} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">CONNECTING</span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="luxury-card p-6" data-testid="market-indices-widget">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-serif text-xl text-white">Market Indices</h2>
            {isConnected ? (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                LIVE
              </span>
            ) : isPolling ? (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gold/20 text-gold text-xs">
                <Radio size={10} className="animate-pulse" />
                POLLING
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-muted/50 text-muted-foreground text-xs">
                <WifiOff size={10} />
                CONNECTING
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-emerald-400 rounded-full"></span> Live data
            </span>
            <span className="mx-2">•</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span> Market estimate
            </span>
          </p>
        </div>
        <button 
          onClick={fetchIndices}
          className="p-2 text-muted-foreground hover:text-gold transition-colors"
          title="Refresh"
          data-testid="refresh-indices-btn"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="space-y-3">
        {indices.map((idx) => {
          const Icon = iconMap[idx.category];
          return (
            <div 
              key={idx.id}
              className={`flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/20 transition-colors ${getFlashClass(idx.id)}`}
              data-testid={`market-index-${idx.category}`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-[#1A1A1A] ${categoryColors[idx.category]}`}>
                  <Icon size={16} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-white font-medium">{idx.name}</p>
                    {idx.data_source === 'live' ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 uppercase">
                        Live
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 uppercase">
                        Est.
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{idx.description || idx.category}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <span className={`flex items-center gap-1.5 px-3 py-1.5 bg-[#111] border border-[#1A1A1A] text-sm font-mono ${idx.change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {getTrendIcon(idx.change_24h)}
                  <span className="text-[10px] text-muted-foreground mr-1">24h</span>
                  {formatChange(idx.change_24h)}
                </span>
                <span className={`flex items-center gap-1.5 px-3 py-1.5 bg-[#111] border border-[#1A1A1A] text-sm font-mono ${idx.change_7d >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  <span className="text-[10px] text-muted-foreground mr-1">7d</span>
                  {formatChange(idx.change_7d)}
                </span>
                <span className={`flex items-center gap-1.5 px-3 py-1.5 bg-[#111] border border-[#1A1A1A] text-sm font-mono ${idx.change_30d >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  <span className="text-[10px] text-muted-foreground mr-1">30d</span>
                  {formatChange(idx.change_30d)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {lastUpdated && (
        <p className="text-xs text-muted-foreground mt-4 text-right">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </p>
      )}
    </div>
  );
};
